import React from 'react';

import { Link } from 'react-router-dom';
import { AlertTriangle, Clock, FileText, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

function SubmitAConcern() {
  return (
    <>

      <div className="max-w-4xl mx-auto py-12 px-6">
        <h1 className="text-3xl font-bold text-[#102A43] mb-8 border-b border-gray-200 pb-4">Submit a Concern</h1>

        <div className="space-y-12">
          
          {/* Introduction */}
          <section>
            <p className="text-gray-800 leading-relaxed mb-6">
              The AASC takes the integrity of its standards seriously. If you suspect an operator is misusing the Declaration Badge, falsely claiming declaration, or operating in violation of the Foundational Control Standards, please report it using the form below.
            </p>
            <div className="bg-red-50 p-4 border border-red-200 rounded-sm flex gap-4 items-start">
              <AlertTriangle className="text-red-700 shrink-0 mt-1" size={20} />
              <div className="text-sm text-red-900">
                <strong>Enforcement Policy:</strong> Verified violations may result in immediate Cease and Desist orders, mandatory removal of marks, suspension of status, and public disclosure of the violation on the Registry.
              </div>
            </div>
          </section>

          {/* Timeline & Process */}
          <section>
            <h2 className="text-xl font-bold text-[#102A43] mb-6 uppercase tracking-wide">Review Process & Outcomes</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="flex items-center gap-2 font-bold text-[#102A43] mb-3">
                  <Clock size={18} /> Timeline
                </h3>
                <p className="text-[#64748B] text-sm mb-4">
                  Reviews typically take <strong>30-60 days</strong> depending on complexity. We prioritize cases involving potential public harm or fraudulent use of marks.
                </p>
              </div>
              <div>
                 <h3 className="flex items-center gap-2 font-bold text-[#102A43] mb-3">
                  <CheckCircle size={18} /> Possible Outcomes
                </h3>
                <ul className="text-[#64748B] text-sm list-disc pl-5 space-y-1">
                  <li><strong>Clarification:</strong> No violation found; minor correction requested.</li>
                  <li><strong>Investigation:</strong> Formal inquiry opened with the operator.</li>
                  <li><strong>Suspension:</strong> Status paused pending remediation.</li>
                  <li><strong>Revocation:</strong> Permanent removal of status.</li>
                  <li><strong>No Action:</strong> Complaint unsubstantiated or out of scope.</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Intake Form (Frontend Only) */}
          <section className="bg-[#F9FAFB] p-8 border border-gray-200 rounded-sm">
             <h2 className="text-xl font-bold text-[#102A43] mb-6">Complaint Intake Form</h2>
             <form className="space-y-6" onSubmit={(e) => e.preventDefault()}>
               <div className="grid md:grid-cols-2 gap-6">
                 <div>
                   <label className="block text-sm font-bold text-[#64748B] mb-2">Subject Organization Name</label>
                   <input type="text" className="w-full p-2 border border-[#E2E8F0] rounded-sm text-[#102A43] bg-white" placeholder="e.g. Acme Corp" />
                 </div>
                 <div>
                   <label className="block text-sm font-bold text-[#64748B] mb-2">Website / URL</label>
                   <input type="url" className="w-full p-2 border border-[#E2E8F0] rounded-sm text-[#102A43] bg-white" placeholder="https://..." />
                 </div>
               </div>

               <div>
                 <label className="block text-sm font-bold text-[#64748B] mb-2">Nature of Concern</label>
                 <select className="w-full p-2 border border-[#E2E8F0] rounded-sm text-[#102A43] bg-white">
                   <option>Select a category...</option>
                   <option>Unauthorized use of Declaration Badge</option>
                   <option>False claim of AASC Status</option>
                   <option>Violation of AI-ABND-1.0 (Boundaries)</option>
                   <option>Violation of AI-HITL-1.0 (Human Authority)</option>
                   <option>Other Standards Violation</option>
                 </select>
               </div>

               <div>
                 <label className="block text-sm font-bold text-[#64748B] mb-2">Description of Issue</label>
                 <textarea className="w-full p-2 border border-[#E2E8F0] rounded-sm h-32 text-[#102A43] bg-white" placeholder="Please provide specific details regarding the alleged violation..."></textarea>
               </div>

               <div className="grid md:grid-cols-2 gap-6">
                 <div>
                   <label className="block text-sm font-bold text-[#64748B] mb-2">Your Name (Optional)</label>
                   <input type="text" className="w-full p-2 border border-[#E2E8F0] rounded-sm text-[#102A43] bg-white" />
                 </div>
                 <div>
                   <label className="block text-sm font-bold text-[#64748B] mb-2">Your Email (Required for follow-up)</label>
                   <input type="email" className="w-full p-2 border border-[#E2E8F0] rounded-sm text-[#102A43] bg-white" />
                 </div>
               </div>

               <div className="pt-4">
                 <Button className="bg-[#2F80ED] hover:bg-[#2563eb] text-white font-bold py-2 px-6 rounded-sm w-full md:w-auto">
                   Submit Report
                 </Button>
               </div>
               <p className="text-xs text-gray-500 mt-2">
                 Reports are confidential. We may contact you for further evidence but will not disclose your identity to the subject organization without consent, unless required by law.
               </p>
             </form>
          </section>

          {/* Contact Info */}
          <section className="text-center pt-8 border-t border-gray-200">
             <p className="text-gray-600 mb-2">For general inquiries regarding enforcement policies:</p>
             <a href="mailto:enforcement@aiaccountabilitycouncil.com" className="text-[#2F80ED] font-bold hover:underline">enforcement@aiaccountabilitycouncil.com</a>
          </section>

        </div>
      </div>
    </>
  );
}

export default SubmitAConcern;